import { getApiUrl } from '../config/api';
import AuthServiceV2 from './authServiceV2';
import { AUTH_ROLES } from '../utils/authConstants';
import { jwtDecode } from 'jwt-decode';
import { apiRequest } from './apiClient';

class UserService {
  // User login - now delegates to AuthServiceV2
  static async userLogin(credentials) {
    return await AuthServiceV2.userLogin(credentials);
  }
  // Get all roles
  static async getRoles() {
    try {
      const result = await apiRequest({
        endpoint: '/admin/api/roles',
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false,
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to fetch roles'
        };
      }
    } catch (error) {
      console.error('Get roles error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  static async updateAdmin(adminId, adminData) {
    try {
      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN);
      if (!token) {
        return {
          success: false,
          message: 'No admin access token found. Please login again.'
        };
      }

      const result = await apiRequest({
        endpoint: `/admin/api/admins/${adminId}`,
        method: 'PATCH',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: adminData,
        showSuccess: false,
        successMessage: 'Admin updated successfully',
        suppressErrorToast: true
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      }

      return {
        success: false,
        message: result.message || 'Failed to update admin'
      };
    } catch (error) {
      console.error('Update admin error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Create a new role
  static async createRole(roleData) {
    try {
      console.log('Creating role with data:', roleData);
      
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.log('No token found in localStorage');
        return {
          success: false,
          message: 'No access token found. Please login again.'
        };
      }

      console.log('Making API call to:', getApiUrl('/admin/api/roles'));

      const result = await apiRequest({
        endpoint: '/admin/api/roles',
        method: 'POST',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: roleData,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Role created successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: result.message || 'Role created successfully!'
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to create role'
        };
      }
    } catch (error) {
      console.error('Create role error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Update a role
  static async updateRole(roleId, roleData) {
    try {
      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN);
      if (!token) {
        return {
          success: false,
          message: 'No admin access token found. Please login again.'
        };
      }

      const result = await apiRequest({
        endpoint: `/admin/api/roles/${roleId}`,
        method: 'PUT',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: roleData,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Role updated successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: result.message || 'Role updated successfully!'
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to update role'
        };
      }
    } catch (error) {
      console.error('Update role error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Delete a role
  static async deleteRole(roleId) {
    try {
      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN);
      if (!token) {
        return {
          success: false,
          message: 'No admin access token found. Please login again.'
        };
      }

      const result = await apiRequest({
        endpoint: `/admin/api/roles/${roleId}`,
        method: 'DELETE',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Role deleted successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          message: result.message || 'Role deleted successfully!'
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to delete role'
        };
      }
    } catch (error) {
      console.error('Delete role error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Get all admins
  static async getAdmins(page = null, limit = null) {
    try {
      let url = '/admin/api/admins';
      
      // Add query parameters if pagination is needed
      const params = new URLSearchParams();
      if (page !== null) params.append('page', page.toString());
      if (limit !== null) params.append('limit', limit.toString());
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const result = await apiRequest({
        endpoint: url,
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false,
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to fetch admins'
        };
      }
    } catch (error) {
      console.error('Get admins error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Get all users
  static async getUsers(page = null, limit = null) {
    try {
      let url = '/admin/api/users';
      
      // Add query parameters if pagination is needed
      const params = new URLSearchParams();
      if (page !== null) params.append('page', page.toString());
      if (limit !== null) params.append('limit', limit.toString());
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const result = await apiRequest({
        endpoint: url,
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false,
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to fetch users'
        };
      }
    } catch (error) {
      console.error('Get users error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Create a new admin
  static async createAdmin(adminData) {
    try {
      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN);
      if (!token) {
        return {
          success: false,
          message: 'No admin access token found. Please login again.'
        };
      }

      console.log('Creating admin with data:', adminData);
      console.log('Request JSON:', JSON.stringify(adminData, null, 2));
      
      // Validate required fields
      const requiredFields = ['name', 'username', 'phone', 'email', 'password']; // Admins don't need designationId/eventId
      const missingFields = requiredFields.filter((field) => {
        const value = adminData[field];
        return value === undefined || value === null || value === '';
      });
      if (missingFields.length > 0) {
        console.log('Missing required fields:', missingFields);
        return {
          success: false,
          message: `Missing required fields: ${missingFields.join(', ')}`
        };
      }

      const result = await apiRequest({
        endpoint: '/admin/api/admins',
        method: 'POST',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: adminData,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Admin created successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      console.log('Admin creation response data:', result);
      
      if (result.success) {
        console.log('✅ Admin creation successful!');
        return {
          success: true,
          data: result.data,
          message: result.message || 'Admin created successfully!'
        };
      } else {
        console.log('❌ Admin creation failed!');
        console.log('Admin creation failed with errors:', result.message);
        return {
          success: false,
          message: Array.isArray(result.message) ? result.message.join(', ') : (result.message || 'Failed to create admin')
        };
      }
    } catch (error) {
      console.error('Create admin error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Create user
  static async createUser(userData) {
    try {
      const result = await apiRequest({
        endpoint: '/admin/api/users',
        method: 'POST',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: {
          name: userData.name,
          username: userData.username,
          email: userData.email,
          phone: userData.phone,
          password: userData.password,
          avatar: userData.avatar,
          address: userData.address,
          designationId: userData.designationId,
          eventId: userData.eventId,
          roleId: userData.roleId,
          status: userData.status
        },
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'User created successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: {
            id: result.data.id,
            name: result.data.name,
            username: result.data.username,
            email: result.data.email,
            phone: result.data.phone,
            avatar: result.data.avatar,
            address: result.data.address,
            status: result.data.status,
            designationId: result.data.designationId,
            createdAt: result.data.created_at,
            updatedAt: result.data.updated_at,
            createdBy: result.data.created_by,
            updatedBy: result.data.updated_by
          }
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to create user'
        };
      }
    } catch (error) {
      console.error('Create user error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Update user
  static async updateUser(userId, userData) {
    try {
      const result = await apiRequest({
        endpoint: `/admin/api/users/${userId}`,
        method: 'PATCH',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: userData,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Updated successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to update user'
        };
      }
    } catch (error) {
      console.error('Update user error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Delete user
  static async deleteUser(userId) {
    try {
      const result = await apiRequest({
        endpoint: `/admin/api/users/${userId}`,
        method: 'DELETE',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Deleted successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          message: 'User deleted successfully'
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to delete user'
        };
      }
    } catch (error) {
      console.error('Delete user error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Delete admin
  static async deleteAdmin(adminId) {
    try {
      const result = await apiRequest({
        endpoint: `/admin/api/admins/${adminId}`,
        method: 'DELETE',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Deleted successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          message: 'Admin deleted successfully'
        };
      } else {
        // Check if it's a 404 (endpoint not found) or other error
        if (result.status_code === 404) {
          return {
            success: false,
            message: 'Admin delete endpoint not available. Please contact system administrator.',
            status_code: 404
          };
        }
        return {
          success: false,
          message: result.message || 'Failed to delete admin'
        };
      }
    } catch (error) {
      console.error('Delete admin error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Designations API methods
  // Get all designations
  static async getDesignations() {
    try {
      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN);
      if (!token) {
        return {
          success: false,
          message: 'No admin access token found. Please login again.'
        };
      }

      const result = await apiRequest({
        endpoint: '/admin/api/admin/designations',
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false,
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to fetch designations'
        };
      }
    } catch (error) {
      console.error('Get designations error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Create a new designation
  static async createDesignation(designationData) {
    try {
      console.log('Creating designation with data:', designationData);
      
      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN);
      if (!token) {
        console.log('No admin token found');
        return {
          success: false,
          message: 'No admin access token found. Please login again.'
        };
      }

      console.log('Making API call to:', getApiUrl('/admin/api/admin/designations'));

      const result = await apiRequest({
        endpoint: '/admin/api/admin/designations',
        method: 'POST',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: designationData,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Designation created successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: result.message || 'Designation created successfully!'
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to create designation'
        };
      }
    } catch (error) {
      console.error('Create designation error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Update a designation
  static async updateDesignation(designationId, designationData) {
    try {
      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN);
      if (!token) {
        return {
          success: false,
          message: 'No admin access token found. Please login again.'
        };
      }

      const result = await apiRequest({
        endpoint: `/admin/api/admin/designations/${designationId}`,
        method: 'PATCH',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: designationData,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Designation updated successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: result.message || 'Designation updated successfully!'
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to update designation'
        };
      }
    } catch (error) {
      console.error('Update designation error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Delete a designation
  static async deleteDesignation(designationId) {
    try {
      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN);
      if (!token) {
        return {
          success: false,
          message: 'No admin access token found. Please login again.'
        };
      }

      const result = await apiRequest({
        endpoint: `/admin/api/admin/designations/${designationId}`,
        method: 'DELETE',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Designation deleted successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          message: result.message || 'Designation deleted successfully!'
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to delete designation'
        };
      }
    } catch (error) {
      console.error('Delete designation error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Events API methods
  // Get all events
  static async getEvents() {
    try {
      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN);
      if (!token) {
        return {
          success: false,
          message: 'No admin access token found. Please login again.'
        };
      }

      const result = await apiRequest({
        endpoint: '/admin/api/events',
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false,
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to fetch events'
        };
      }
    } catch (error) {
      console.error('Get events error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Create a new event
  static async createEvent(eventData) {
    try {
      console.log('Creating event with data:', eventData);
      
      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN);
      if (!token) {
        console.log('No admin token found');
        return {
          success: false,
          message: 'No admin access token found. Please login again.'
        };
      }

      console.log('Making API call to:', getApiUrl('/admin/api/events'));

      const result = await apiRequest({
        endpoint: '/admin/api/events',
        method: 'POST',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: eventData,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Event created successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: result.message || 'Event created successfully!'
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to create event'
        };
      }
    } catch (error) {
      console.error('Create event error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Update an event
  static async updateEvent(eventId, eventData) {
    try {
      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN);
      if (!token) {
        return {
          success: false,
          message: 'No admin access token found. Please login again.'
        };
      }

      const result = await apiRequest({
        endpoint: `/admin/api/events/${eventId}`,
        method: 'PATCH',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: eventData,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Event updated successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: result.message || 'Event updated successfully!'
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to update event'
        };
      }
    } catch (error) {
      console.error('Update event error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Delete an event
  static async deleteEvent(eventId) {
    try {
      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN);
      if (!token) {
        return {
          success: false,
          message: 'No admin access token found. Please login again.'
        };
      }

      const result = await apiRequest({
        endpoint: `/admin/api/events/${eventId}`,
        method: 'DELETE',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Event deleted successfully',
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          message: result.message || 'Event deleted successfully!'
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to delete event'
        };
      }
    } catch (error) {
      console.error('Delete event error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Get current user info
  static async getCurrentUser() {
    try {
      // Check if user token exists and is valid before making API call
      const token = AuthServiceV2.getToken(AUTH_ROLES.USER);
      if (!token) {
        console.log('No user token found');
        return {
          success: false,
          message: 'No authentication token found'
        };
      }

      // Validate token before making request
      const isTokenValid = AuthServiceV2.isTokenValid(token, AUTH_ROLES.USER);
      if (!isTokenValid) {
        console.log('User token is expired or invalid');
        return {
          success: false,
          message: 'Authentication token has expired'
        };
      }

      const result = await apiRequest({
        endpoint: '/admin/api/public/users/me',
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.USER, // Explicitly use user role for this public endpoint
        showSuccess: false,
        suppressErrorToast: true // Let component handle error toasts
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to fetch user info'
        };
      }
    } catch (error) {
      console.error('Get current user error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }
}

export default UserService;
